import * as React from 'react';
import { Group, Drawer, Badge, Tooltip, ActionIcon, Text, Button, Collapse, Box, createStyles } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { UseFormReturnType } from '@mantine/form';
import { ColumnDef } from '@tanstack/react-table';
import { IconEdit, IconChevronRight, IconUser, IconUsers, IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { openConfirmModal } from '@mantine/modals';
import { useTranslation, Trans } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import uuid4 from 'uuid4';

// Components
import { TableStatic } from '../Table/TableStatic';
import { ContactForm, ContactFormHandle } from '../ContactForm';
import { FormScaffolding, FormHandle } from '../Form/FormScaffolding';

// Hooks
import { useStaticKey } from '../Form/hooks/useStaticKey';
import { useCompleteness } from '../Form/hooks/useCompleteness';

// Helpers
import { transformContactRecordToForm } from '../../containers/Admin/Contacts/helpers';

// Types
import { IManifestFieldHydrated } from '../../manifests/types';
import { IContact, IContactRecord, WithCompleteness } from '../../types';

const useStyles = createStyles((theme) => ({
    box: {
        margin: '0 -1.25rem',
        borderTop: '1px solid #dee2e6',
        borderBottom: '1px solid #dee2e6',
        overflow: 'hidden',
        borderRadius: '0 0 6px 6px'
    },
    boxConfirmPrimary: {
        marginLeft: '-1.25rem',
        maxWidth: '100%'
    },
    icon: {
        transition: 'all 0.2s ease'
    },
    iconRotated: {
        transform: 'rotate(90deg)'
    },
    toggleGroup: {
        cursor: 'pointer',
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem',
        background: '#f9fcff',
        color: theme.colors.blue[8]
        // background: theme.colors.blue[0]
    },
    toggleGroupLabelText: {
        display: 'flex',
        alignItems: 'center'
    },
    missingInfoIcon: {
        position: 'absolute',
        left: '6px',
        top: '50%',
        marginTop: '-0.8rem'
    },
    drawerHeader: {
        zIndex: 1
    },
    drawerInner: {
        left: 0
    },
    sectionDescription: {
        fontSize: theme.fontSizes.sm,
        whiteSpace: 'pre-line'
    }
}));

interface ContactsModuleProps<FormValues> {
    formId?: string;
    data?: IContactRecord[];
    subManifest: IManifestFieldHydrated[];
    parentform: UseFormReturnType<FormValues>;
    ancillaryData?: Record<string, any>;
    readOnly: boolean;
    collapsible?: boolean;
    hidden?: boolean;
    targetChildId?: string;
    targetStateId?: string;
    parentNext?: () => Promise<void>;
    parentPrev?: () => void;
    required?: boolean;
    activeStep?: number;
    clientId?: string;
    description?: string;
    trigger?: string;
}

export function ContactsModule<
    FormValues extends {
        contactsModule?: any[];
        primaryContact?: string;
        financialContact?: string;
    }
>(props: ContactsModuleProps<FormValues>) {
    const { classes, cx } = useStyles();
    const {
        formId,
        data,
        subManifest,
        parentform,
        readOnly,
        collapsible,
        hidden,
        targetChildId,
        targetStateId,
        parentNext,
        parentPrev,
        activeStep,
        clientId,
        description,
        ancillaryData,
        trigger
    } = props;
    const ContactFormRef = React.useRef<ContactFormHandle>(null);
    const { getNameByStaticKey, getStaticKeyByName } = useStaticKey();
    const { computeCompleteness } = useCompleteness();
    const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
    const [tableOpened, { toggle: toggleTable }] = useDisclosure(!collapsible);
    const { t, i18n: i18next } = useTranslation();
    const { t: columnHeaderTrans } = useTranslation('components', { keyPrefix: 'tables.contacts.columns' });
    const FormRef = React.useRef<FormHandle>(null);
    const [editingContactIndex, setEditingContactIndex] = React.useState<number | undefined>();
    const openDiscardChangesModal = React.useCallback(
        ({ confirmCallback }: { confirmCallback: () => void }) => {
            openConfirmModal({
                title: t('modals.confirmDiscardChanges.title', { ns: 'components' }),
                children: <Text size="sm">{t('modals.confirmDiscardChanges.body', { ns: 'components' })}</Text>,
                labels: {
                    confirm: t('modals.confirmDiscardChanges.confirmCTA', { ns: 'components' }),
                    cancel: t('cancel', { ns: 'common' })
                },
                confirmProps: { color: 'red' },
                onCancel: () => console.log('Cancel'),
                onConfirm: () => {
                    confirmCallback();
                }
            });
        },
        [setEditingContactIndex]
    );
    const primaryContactId = React.useMemo(
        () => parentform?.values?.primaryContact,
        [parentform?.values?.primaryContact]
    );
    const financialContactId = React.useMemo(
        () => parentform?.values?.financialContact,
        [parentform?.values?.financialContact]
    );

    const columns = React.useMemo<ColumnDef<WithCompleteness<IContact>>[]>(
        () => [
            {
                accessorKey: 'completeness',
                header: () => '',
                footer: (props) => props.column.id,
                enableColumnFilter: false,
                accessorFn: (row) => (row.completeness ?? 0) * 100,
                size: 1,
                cell: (props) => (
                    <Group
                        className={classes.missingInfoIcon}
                        // position="right"
                        style={props.getValue() !== 100 ? {} : { pointerEvents: 'none', opacity: 0 }}
                    >
                        <Tooltip label="Missing information" withArrow>
                            <ActionIcon color="orange">
                                <IconAlertCircle size="1rem" />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                )
            },
            {
                accessorFn: (row) => row?.name,
                id: 'name',
                header: () => columnHeaderTrans('name'),
                footer: (props) => props.column.id,
                enableColumnFilter: false
            },
            {
                accessorFn: (row) => row?.email,
                id: 'email',
                header: () => columnHeaderTrans('primaryEmail'),
                footer: (props) => props.column.id,
                enableColumnFilter: false
            },
            {
                id: 'pointOfContact',
                footer: (props) => props.column.id,
                enableColumnFilter: false,
                cell: (props) => {
                    return (
                        <Group position="center">
                            {primaryContactId === props.row.original.id ? (
                                <Badge size="xs" color="cyan">
                                    {t('primary', { ns: 'common' })}
                                </Badge>
                            ) : null}
                            {financialContactId === props.row.original.id ? (
                                <Badge size="xs" color="green">
                                    {t('financial', { ns: 'common' })}
                                </Badge>
                            ) : null}
                        </Group>
                    );
                }
            },
            {
                id: 'actions',
                enableColumnFilter: false,
                cell: (props) => (
                    <Group position="right" style={{ flexWrap: 'nowrap' }}>
                        <ActionIcon
                            variant={'light'}
                            color={'blue'}
                            // color={isIncomplete ? 'orange' : 'blue'}
                            onClick={() => {
                                setEditingContactIndex(props.row.index);
                            }}
                        >
                            <IconEdit size={14} />
                        </ActionIcon>
                    </Group>
                )
            }
        ],
        [openDiscardChangesModal, FormRef, primaryContactId, financialContactId]
    );
    const closeContactForm = () => {
        closeDrawer();
        setEditingContactIndex(undefined);
    };
    const contactsRows = React.useMemo(() => {
        return (data ?? []).map((contact, index) => {
            return {
                name: contact?.name?.fullName,
                email: contact?.email?.primary,
                id: contact.id,
                completeness: computeCompleteness({
                    manifest: subManifest,
                    formState: transformContactRecordToForm(contact),
                    ancillaryData: {
                        isPrimaryContact: ancillaryData?.organization?.primaryContact === contact.id,
                        ...ancillaryData
                    }
                }),
                index
            };
        });
    }, [parentform, data, ancillaryData]);

    React.useEffect(() => {
        if (targetChildId && contactsRows.length) {
            const targetChildIndex = contactsRows.findIndex(({ id }) => id === targetChildId);
            setEditingContactIndex(targetChildIndex);
        } else if (targetStateId && contactsRows.length) {
            const targetStateName = getNameByStaticKey(targetStateId) as 'primaryContact' | 'financialContact';
            const targetId = parentform?.values?.[targetStateName];
            const targetChildIndex = contactsRows.findIndex(({ id }) => id === targetId);

            setEditingContactIndex(targetChildIndex);
        }
    }, [contactsRows, targetChildId, targetStateId]);

    const editingContact = React.useMemo(() => {
        if (typeof editingContactIndex === 'undefined') return null;
        if (typeof parentform?.values?.contactsModule?.[editingContactIndex] !== 'undefined') {
            return parentform.values.contactsModule[editingContactIndex];
        } else {
            return {};
        }
    }, [editingContactIndex, activeStep]);

    React.useEffect(() => {
        if (typeof editingContactIndex !== 'undefined') {
            openDrawer();
        }
    }, [editingContactIndex]);

    const drawerTitle = React.useMemo(
        () =>
            editingContact && Object.keys(editingContact).length
                ? t('contactsModule.editingContact', { ns: 'components' })
                : t('contactsModule.newContact', { ns: 'components' }),
        [editingContact]
    );

    if (hidden) return null;
    return !(targetChildId || targetStateId) ? (
        <>
            {description ? (
                <Text mb="md" color="dimmed" className={classes.sectionDescription}>
                    {i18next.exists(`surveys:sectionDescriptions.${description}`)
                        ? t(`sectionDescriptions.${description}`, { ns: 'surveys' })
                        : description}
                </Text>
            ) : null}
            <Box className={classes.box}>
                {collapsible ? (
                    <Group py="md" position="apart" onClick={toggleTable} className={classes.toggleGroup}>
                        <Group>
                            {contactsRows.length > 1 ? <IconUsers size={14} /> : <IconUser size={14} />}
                            <Text size="sm" fw={500}>
                                <Trans
                                    t={t}
                                    i18nKey={
                                        contactsRows.length > 1
                                            ? 'contactsModule.toggleLabelPlural'
                                            : 'contactsModule.toggleLabelSingular'
                                    }
                                    ns="components"
                                    values={{
                                        contactCount: contactsRows.length
                                    }}
                                ></Trans>
                            </Text>
                        </Group>
                        <IconChevronRight
                            size={14}
                            className={cx(classes.icon, {
                                [classes.iconRotated]: tableOpened
                            })}
                        />
                    </Group>
                ) : null}
                <Collapse
                    in={tableOpened}
                    style={{
                        borderTop: '1px solid #dee2e6'
                    }}
                >
                    <TableStatic<IContact>
                        visibleColumns={{}}
                        data={contactsRows}
                        columns={columns}
                        rightControl={
                            !readOnly ? (
                                <Button
                                    variant="default"
                                    onClick={() => {
                                        setEditingContactIndex(contactsRows.length);
                                    }}
                                    leftIcon={<IconPlus size={14} />}
                                >
                                    {t('contactsModule.newContact', { ns: 'components' })}
                                </Button>
                            ) : null
                        }
                        searchable
                        noMargin
                    />
                    <Drawer
                        opened={drawerOpened}
                        onClose={() => {
                            if (typeof ContactFormRef?.current?.onCancel === 'function') {
                                ContactFormRef.current.onCancel();
                            } else {
                                closeContactForm();
                            }
                        }}
                        title={drawerTitle}
                        position="right"
                        size="xl"
                        overlayProps={{ opacity: 0.5, blur: 4 }}
                        classNames={{
                            header: classes.drawerHeader,
                            inner: classes.drawerInner
                        }}
                    >
                        {typeof editingContactIndex !== 'undefined' ? (
                            <ContactForm
                                trigger={trigger}
                                ref={ContactFormRef}
                                ancillaryData={ancillaryData}
                                clientId={clientId}
                                isNew={!data?.[editingContactIndex]?.id}
                                contactId={data?.[editingContactIndex]?.id}
                                onCancel={closeContactForm}
                                onSave={closeContactForm}
                                manifestSections={[{ manifest: subManifest }]}
                                surveyId={formId}
                            />
                        ) : null}
                    </Drawer>
                </Collapse>
            </Box>
        </>
    ) : (
        <ContactForm
            trigger={trigger}
            isNew={Boolean(typeof editingContactIndex !== 'undefined' && !data?.[editingContactIndex]?.id)}
            contactId={typeof editingContactIndex !== 'undefined' ? data?.[editingContactIndex]?.id : ''}
            onCancel={closeContactForm}
            onSave={closeContactForm}
            manifestSections={[{ manifest: subManifest }]}
            ancillaryData={ancillaryData}
            isSubForm
            noCardStyle
            surveyId={formId}
            clientId={clientId}
        />
    );
}
